import React, { useState } from 'react';
import Button from './Button';

const getInitials = (name = '') => name.split(' ').map(w => w[0]).join('').toUpperCase();

const Avatar = ({ person, index, zBase = 10, borderColor = '#1e1b3a', onClick }) => (
  person.image ? (
    <img
      key={index}
      src={person.image}
      alt={person.name}
      title={person.name + ' — Click to view team'}
      onClick={onClick}
      style={{
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        border: `2px solid ${borderColor}`,
        objectFit: 'cover',
        marginLeft: index === 0 ? 0 : '-12px',
        position: 'relative',
        zIndex: zBase + index,
        cursor: 'pointer',
      }}
    />
  ) : (
    <div
      key={index}
      title={person.name + ' — Click to view team'}
      onClick={onClick}
      style={{
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        backgroundColor: borderColor,
        color: '#fff',
        fontSize: '0.7rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `2px solid ${borderColor}`,
        marginLeft: index === 0 ? 0 : '-12px',
        position: 'relative',
        zIndex: zBase + index,
        cursor: 'pointer',
      }}
    >
      {getInitials(person.name)}
    </div>
  )
);

const ResearchCard = ({
  title,
  description,
  status,
  people = [],
  advisors = [],
  slug,
  group,
  orgs = [],
  chapterSlug,
  chapter,
}) => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const hasRealSponsor = orgs.some(org => org.name === 'Sponsor' && org.image && org.image !== '/question-mark.webp');
  const showSponsorship = !hasRealSponsor && status.toLowerCase() !== 'completed';
  const sponsorMessage = showSponsorship ? 'Sponsorship available' : '';
  const combinedStatus = sponsorMessage ? `${status} · ${sponsorMessage}` : status;

  const projectUrl = chapterSlug
    ? `/chapters/${chapterSlug}?section=projects&project=${slug}`
    : `/projects/${slug}`;

  return (
    <div
      style={{
        background: '#0F0F1A',
        padding: '2rem',
        borderRadius: '1rem',
        color: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        minHeight: '400px',
        maxWidth: '400px',
        flex: '1 1 300px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        border: '1px solid rgba(99, 102, 241, 0.3)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', top: '1rem', left: '1rem', display: 'flex', gap: '0.5rem' }}>
        {orgs.map((org, index) => {
          const isChapterOrg = org.name === chapter;

          return (
            <img
              key={index}
              src={org.image}
              alt={org.name}
              title={isChapterOrg ? `Chapter: ${chapter}` : org.name}
              style={{
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                border: '1.5px solid #fff',
                objectFit: 'cover',
                backgroundColor: '#ffffff',
              }}
            />
          );
        })}
      </div>

      <div style={{ fontSize: '0.85rem', color: '#a5b4fc', marginTop: '2rem', marginBottom: '0.5rem' }}>
        {combinedStatus}
      </div>

      <h1 className="title">{title}</h1>
      <p className="content-text">{description}</p>

      <div style={{ flexGrow: 1 }}></div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', marginRight: '1rem', position: 'relative' }}>
          {people.slice(0, 5).map((person, i) => (
            <Avatar key={`person-${i}`} person={person} index={i} zBase={10} borderColor="#1e1b3a" onClick={() => setShowModal(true)} />
          ))}
        </div>
        <div style={{ display: 'flex', position: 'relative' }}>
          {advisors.slice(0, 4).map((advisor, i) => (
            <Avatar key={`advisor-${i}`} person={advisor} index={i} zBase={20} borderColor="#4f46e5" onClick={() => setShowModal(true)} />
          ))}
        </div>
      </div>

      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ background: '#1e1b3a', padding: '1.5rem', borderRadius: '1rem', maxWidth: '500px', width: '90%', maxHeight: '90vh', overflowY: 'auto', padding: ' .5rem 1rem 1rem 1rem' }}>
            <h1 className='title' style={{ fontSize: '1.5rem' }}>Research Team</h1>

            <h2 className='subtitle' style={{ textAlign: 'left', color: '#ffffff', fontSize: '1rem', marginBottom: '0.5rem' }}>Researchers:</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: advisors.length ? '1rem' : '0' }}>
              {people.map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {p.linkedin ? (
                    <a
                      href={p.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                    >
                      {p.image && (
                        <img
                          src={p.image}
                          alt={p.name}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '1px solid #a5b4fc',
                          }}
                        />
                      )}
                      <span style={{ fontSize: '0.9rem' }}>{p.name} ↗</span>
                    </a>
                  ) : (
                    <>
                      {p.image && (
                        <img
                          src={p.image}
                          alt={p.name}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '1px solid #a5b4fc',
                          }}
                        />
                      )}
                      <span style={{ fontSize: '0.9rem' }}>{p.name}</span>
                    </>
                  )}
                </div>
              ))}

            </div>

            {advisors.length > 0 && (
              <>
                {advisors.length > 1 && (
                  <h2 className='subtitle' style={{ textAlign: 'left', color: '#ffffff', fontSize: '1rem', marginBottom: '0.5rem' }}>Advisors:</h2>
                )}
                {advisors.length === 1 && (
                  <h2 className='subtitle' style={{ textAlign: 'left', color: '#ffffff', fontSize: '1rem', marginBottom: '0.5rem' }}>Advisor:</h2>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {advisors.map((a, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {a.linkedin ? (
            <a
              href={a.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}
            >
              {a.image && (
                <img
                  src={a.image}
                  alt={a.name}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '1px solid #c084fc',
                  }}
                />
              )}
              <span style={{ fontSize: '0.9rem' }}>{a.name} ↗</span>
            </a>
          ) : (
            <>
              {a.image && (
                <img
                  src={a.image}
                  alt={a.name}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '1px solid #c084fc',
                  }}
                />
              )}
              <span style={{ fontSize: '0.9rem' }}>{a.name}</span>
            </>
          )}
        </div>
      ))}
                </div>
              </>
            )}

            <Button label="Close" onClick={() => setShowModal(false)} style={{ marginTop: '1rem' }} />
          </div>
        </div>
      )}

      <a
        href={projectUrl}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ marginTop: 'auto', textDecoration: 'none', display: 'block' }}
      >
        <img
          src={group}
          alt="Research group"
          style={{
            width: '100%',
            borderRadius: '1rem',
            objectFit: 'cover',
            borderBottomLeftRadius: '0',
            borderBottomRightRadius: '0',
            marginBottom: '0',
            boxSizing: 'border-box',
            border: '1px solid #333',
          }}
        />
      </a>

      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ display: 'flex', justifyContent: 'center', marginTop: '-.5rem' }}
      >
        <Button
          label="Learn More"
          to={projectUrl}
          style={{
            display: 'block',
            width: '100%',
            borderTopRightRadius: '0',
            borderTopLeftRadius: '0',
            backgroundColor: hovered ? '#a21caf' : '#c026d3',
            color: '#ffffff',
            border: '1px solid #333',
          }}
        />
      </div>
    </div>
  );
};

export default ResearchCard;